import { useMemo } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { ArrowRight, Clock, Folder, KeyRound, Keyboard, Lock, ShieldAlert, Star, Timer, Trash2, Wand2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { paths } from "../../app/paths.js";
import { HIDDEN_LOCK_GRACE_MS, useAutoLockMinutes, useClipboardSeconds, useLockOnHide } from "../../app/preferences.js";
import { useRecentUse } from "../../app/recentUse.js";
import { useSession } from "../../app/session.js";
import { useAccountKeys } from "../../data/account.js";
import { useTwoFactorStatus, usePasskeys } from "../../data/security.js";
import { useTrashItems, useVaultEntries } from "../../data/vault.js";
import { folderCounts, healthInputs } from "../../lib/entries.js";
import type { Entry, FolderCount } from "../../lib/entries.js";
import { analyzeHealth } from "../../lib/health.js";
import { useBreachScan } from "../../data/breach.js";
import { formatDateTime, formatRelativeTime, greetingName } from "../../lib/format.js";
import type { RecentUse } from "../../lib/recent.js";
import { Avatar, Card, CardHeader, Kbd, Page, PageHeader, buttonClass } from "../../ui/primitives.js";
import { modifierLabel } from "../../shell/shortcuts.js";
import { FavoritesPanel, RecentlyUsedPanel } from "./QuickAccess.js";
import type { RecentItem } from "./QuickAccess.js";
import { QuickGenerator } from "./QuickGenerator.js";
import { SecurityOverview } from "./SecurityOverview.js";

const FAVORITES_SHOWN = 8;
const RECENT_SHOWN = 6;

function StatTile(props: {
  readonly to: string;
  readonly label: string;
  readonly value: number | undefined;
  readonly icon: LucideIcon;
  readonly alert?: boolean | undefined;
}): ReactNode {
  const Icon = props.icon;
  return (
    <Link
      to={props.to}
      className="group flex items-center gap-3 rounded-xl border border-line bg-ink-2 px-3.5 py-3.5 sm:gap-3.5 sm:px-4 transition-colors hover:border-line-strong hover:bg-ink-3/50"
    >
      <span
        className={`grid size-10 shrink-0 place-items-center rounded-lg border ${
          props.alert === true ? "border-buoy/30 bg-buoy/10 text-buoy" : "border-line bg-ink-3 text-flow"
        }`}
      >
        <Icon size={17} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[0.8125rem] text-mist">{props.label}</span>
        <span className="font-display mt-0.5 block text-[1.6rem] font-bold tabular-nums leading-none text-foam">
          {props.value === undefined ? "–" : props.value}
        </span>
      </span>
      <ArrowRight size={15} aria-hidden="true" className="hidden shrink-0 text-mist-2 sm:block transition-transform group-hover:translate-x-0.5 group-hover:text-flow" />
    </Link>
  );
}

function FoldersPanel(props: { readonly folders: ReadonlyArray<FolderCount> }): ReactNode {
  const max = Math.max(1, ...props.folders.map((f: FolderCount): number => f.count));
  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <CardHeader title="Ordner" description={`${props.folders.length} Ordner im Tresor`} icon={Folder} />
      {props.folders.length === 0 ? (
        <p className="px-5 py-8 text-sm text-mist">Ordner entstehen automatisch, sobald du Einträgen einen zuweist.</p>
      ) : (
        <ul className="flex flex-col gap-0.5 p-2.5">
          {props.folders.slice(0, 7).map((folder: FolderCount) => (
            <li key={folder.name}>
              <Link
                to={`/vault?${new URLSearchParams({ folder: folder.name }).toString()}`}
                className="group flex flex-col gap-1.5 rounded-md px-2.5 py-2 transition-colors hover:bg-foam/[0.04]"
              >
                <span className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate text-foam">{folder.name}</span>
                  <span className="shrink-0 text-xs tabular-nums text-mist-2">{folder.count}</span>
                </span>
                <span className="h-1 overflow-hidden rounded-full bg-ink-4" aria-hidden="true">
                  <span className="block h-full rounded-full bg-flow/70 transition-colors group-hover:bg-flow" style={{ width: `${(folder.count / max) * 100}%` }} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RecentChangesPanel(props: { readonly entries: ReadonlyArray<Entry>; readonly now: number; readonly loading: boolean }): ReactNode {
  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <CardHeader
        title="Zuletzt geändert"
        icon={Clock}
        action={
          <Link to={paths.vault} className="whitespace-nowrap text-sm text-mist transition-colors hover:text-flow">
            Alle
          </Link>
        }
      />
      <ul className="divide-y divide-line">
        {props.entries.length === 0 && !props.loading ? <li className="px-5 py-8 text-sm text-mist">Noch keine Einträge.</li> : null}
        {props.entries.map((entry: Entry) => (
          <li key={entry.meta.id}>
            <Link to={paths.vaultItem(entry.meta.id)} className="flex items-center gap-3 px-5 py-2.5 transition-colors hover:bg-foam/[0.03]">
              <Avatar label={entry.data.title} size="sm" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foam">{entry.data.title}</span>
              <span className="shrink-0 text-xs text-mist-2" title={formatDateTime(entry.meta.updatedAt)}>
                {formatRelativeTime(entry.meta.updatedAt, props.now)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SessionPanel(): ReactNode {
  const autoLock = useAutoLockMinutes();
  const clipboard = useClipboardSeconds();
  const lockOnHide = useLockOnHide();
  const mod = modifierLabel();
  const rows: ReadonlyArray<{ readonly icon: LucideIcon; readonly label: string; readonly value: string }> = [
    { icon: Timer, label: "Automatische Sperre", value: `${autoLock} Min.` },
    { icon: Lock, label: "Sperre im Hintergrund", value: lockOnHide ? `nach ${HIDDEN_LOCK_GRACE_MS / 1000} s` : "aus" },
    { icon: KeyRound, label: "Zwischenablage leeren", value: `${clipboard} s` }
  ];
  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <CardHeader
        title="Sitzungsschutz"
        icon={Lock}
        action={
          <Link to={paths.settings} className="whitespace-nowrap text-sm text-mist transition-colors hover:text-flow">
            Anpassen
          </Link>
        }
      />
      <dl className="divide-y divide-line">
        {rows.map((row) => {
          const Icon = row.icon;
          return (
            <div key={row.label} className="flex items-center gap-3 px-5 py-2.5 text-sm">
              <Icon size={15} aria-hidden="true" className="shrink-0 text-mist-2" />
              <dt className="min-w-0 flex-1 truncate text-mist">{row.label}</dt>
              <dd className="shrink-0 font-medium tabular-nums text-foam">{row.value}</dd>
            </div>
          );
        })}
      </dl>
      <div className="mt-auto border-t border-line px-5 py-3.5 text-xs leading-relaxed text-mist-2">
        <p className="flex flex-wrap items-center gap-1.5">
          <Keyboard size={13} aria-hidden="true" />
          Sofort sperren mit <Kbd>{mod}</Kbd>
          <Kbd>⇧</Kbd>
          <Kbd>L</Kbd> · alle Kürzel mit <Kbd>?</Kbd>
        </p>
      </div>
    </Card>
  );
}

export function DashboardPage(): ReactNode {
  useDocumentTitle("Übersicht");
  const { state } = useSession();
  const entries = useVaultEntries();
  const trash = useTrashItems();
  const twoFactor = useTwoFactorStatus();
  const passkeys = usePasskeys();
  const accountKeys = useAccountKeys();
  const recentUse = useRecentUse();

  const all: ReadonlyArray<Entry> = useMemo((): ReadonlyArray<Entry> => entries.data?.entries ?? [], [entries.data]);
  const now = useMemo((): number => Date.now(), [entries.dataUpdatedAt, recentUse]);
  const breach = useBreachScan(all);
  const health = useMemo(
    (): ReturnType<typeof analyzeHealth> =>
      analyzeHealth(healthInputs(all), now, breach.breached),
    [all, now, breach.breached]
  );

  const byId = useMemo((): ReadonlyMap<string, Entry> => new Map(all.map((e: Entry): [string, Entry] => [e.meta.id, e])), [all]);
  const recentItems: ReadonlyArray<RecentItem> = recentUse
    .map((use: RecentUse): RecentItem | undefined => {
      const entry = byId.get(use.id);
      return entry === undefined ? undefined : { entry, at: use.at };
    })
    .filter((item: RecentItem | undefined): item is RecentItem => item !== undefined)
    .slice(0, RECENT_SHOWN);

  const lastUsed = new Map(recentUse.map((use: RecentUse): [string, number] => [use.id, use.at]));
  const allFavorites = all.filter((e: Entry): boolean => e.meta.favorite);
  const favorites = [...allFavorites]
    .sort((a: Entry, b: Entry): number =>
      (lastUsed.get(b.meta.id) ?? 0) - (lastUsed.get(a.meta.id) ?? 0) || a.data.title.localeCompare(b.data.title, "de", { sensitivity: "base" })
    )
    .slice(0, FAVORITES_SHOWN);
  const recentChanges = [...all].sort((a: Entry, b: Entry): number => b.meta.updatedAt - a.meta.updatedAt).slice(0, 6);
  const folders = folderCounts(all);

  const name = state.status === "locked" ? "" : greetingName(state.email);
  const loading = entries.isPending;
  const loaded = entries.data !== undefined;

  return (
    <Page width="wide">
      <PageHeader
        eyebrow={new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "numeric", month: "long" }).format(new Date())}
        title={name !== "" ? `Hallo, ${name}` : "Übersicht"}
        description="Zustand deines Tresors, Schnellzugriff und offene Sicherheitsaufgaben auf einen Blick."
        divider={false}
        actions={
          <Link to={paths.generator} className={buttonClass("secondary")}>
            <Wand2 size={15} aria-hidden="true" /> Generator
          </Link>
        }
      />

      <section aria-label="Kennzahlen" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile to={paths.vault} label="Einträge" value={loaded ? all.length : undefined} icon={KeyRound} />
        <StatTile to="/vault?view=favorites" label="Favoriten" value={loaded ? allFavorites.length : undefined} icon={Star} />
        <StatTile
          to={
            health.breachedIds.length > 0
              ? "/vault?filter=breached"
              : health.weakIds.length > 0
                ? "/vault?filter=weak"
                : health.reusedIds.length > 0
                  ? "/vault?filter=reused"
                  : "/vault?filter=old"
          }
          label="Zu prüfen"
          value={loaded ? health.issueIds.length : undefined}
          icon={ShieldAlert}
          alert={health.issueIds.length > 0}
        />
        <StatTile to="/vault?view=trash" label="Papierkorb" value={trash.data?.length} icon={Trash2} />
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SecurityOverview
            health={health}
            breach={breach.status}
            loading={loading}
            account={{
              totpEnabled: twoFactor.data === undefined ? undefined : twoFactor.data.totp === "enabled",
              passkeys: passkeys.data?.length,
              backupCodes: twoFactor.data?.backupCodesRemaining,
              recoveryKit: accountKeys.data === undefined ? undefined : accountKeys.data.recoveryCreatedAt !== null
            }}
          />
        </div>
        <QuickGenerator />

        <div className="lg:col-span-2">
          <FavoritesPanel favorites={favorites} total={allFavorites.length} loading={loading} />
        </div>
        <RecentlyUsedPanel items={recentItems} now={now} loading={loading} />

        <FoldersPanel folders={folders} />
        <RecentChangesPanel entries={recentChanges} now={now} loading={loading} />
        <SessionPanel />
      </div>
    </Page>
  );
}
